import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const errorParam = params.get("error");
        const errorDescription = params.get("error_description");

        if (errorParam) {
          console.error("Auth error:", errorParam, errorDescription);
          // Distinguish an expired/invalid confirmation link from other auth errors
          // so the auth modal can offer a tailored resend flow.
          const isExpiredLink =
            errorParam === "access_denied" &&
            (errorDescription || "")
              .toLowerCase()
              .includes("email link is invalid or has expired");
          if (isExpiredLink) {
            navigate("/?error=link_expired");
          } else {
            navigate(`/?error=${encodeURIComponent(errorParam)}`);
          }
          return;
        }

        if (code) {
          // PKCE flow: explicitly exchange the code for a session.
          // Relying solely on onAuthStateChange has a race condition where
          // INITIAL_SESSION fires before the exchange completes.
          const { data, error } =
            await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            console.error("Failed to exchange code for session:", error);
            navigate("/?error=auth_failed");
            return;
          }
          if (data.session) {
            const user = data.session.user;
            const hasCompletedOnboarding =
              user?.user_metadata?.onboarding_completed;
            if (!hasCompletedOnboarding) {
              sessionStorage.setItem("triggerOnboarding", "true");
              sessionStorage.setItem("onboardingEmail", user.email || "");
              navigate("/");
            } else {
              const intendedDestination =
                sessionStorage.getItem("redirectAfterAuth") || "/dashboard";
              sessionStorage.removeItem("redirectAfterAuth");
              navigate(intendedDestination);
            }
            return;
          }
        }

        // No code param — check for an existing session (e.g. OAuth implicit flow)
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session) {
          const user = sessionData.session.user;
          const hasCompletedOnboarding =
            user?.user_metadata?.onboarding_completed;
          if (!hasCompletedOnboarding) {
            sessionStorage.setItem("triggerOnboarding", "true");
            sessionStorage.setItem("onboardingEmail", user.email || "");
            navigate("/");
          } else {
            const intendedDestination =
              sessionStorage.getItem("redirectAfterAuth") || "/dashboard";
            sessionStorage.removeItem("redirectAfterAuth");
            navigate(intendedDestination);
          }
        } else {
          navigate("/?error=auth_failed");
        }
      } catch (error) {
        console.error("Unexpected error in auth callback:", error);
        navigate("/?error=unexpected_error");
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#40e0d0] mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-700">
          Completing sign-in...
        </h2>
        <p className="text-gray-500">
          Please wait while we set up your account.
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
